import React, { useMemo } from 'react';

import { Avatar, Box } from '@mui/material';
import { alpha, darken, lighten, useTheme } from '@mui/material/styles';

import clsx from 'clsx';

import { AvatarTypes } from '../../../../shared/constants';
import { useStyles } from './AppAvatar.styles';

export interface IAppAvatarProps {
  src?: string;
  className?: string;
  type?: AvatarTypes;
  size?: number;
  children?: React.ReactNode;
  border?: 'primary' | 'secondary' | 'white' | 'none';
  fallbackSeedId?: string | number;
  fallbackSeedName?: string;
  onClick?: () => void;
}

const FALLBACK_AVATAR_COLORS = [
  '#EF5350',
  '#EC407A',
  '#AB47BC',
  '#7E57C2',
  '#5C6BC0',
  '#42A5F5',
  '#26A69A',
  '#66BB6A',
  '#FFA726',
  '#8D6E63',
];

const DEFAULT_FALLBACK_SEED = 'app-avatar-default-seed';

function hashSeed(value: string): number {
  let hash = 5381;

  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }

  return Math.abs(hash >>> 0);
}

const AppAvatar: React.FC<IAppAvatarProps> = ({
  src,
  className,
  type = AvatarTypes.square,
  size = 40,
  children,
  border = 'primary',
  fallbackSeedId,
  fallbackSeedName,
  onClick = () => {},
}) => {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const fallbackBackgroundImage = useMemo(() => {
    if (src) return undefined;

    const seed =
      fallbackSeedId !== undefined && fallbackSeedId !== null
        ? String(fallbackSeedId)
        : fallbackSeedName || DEFAULT_FALLBACK_SEED;

    const colorIndex = hashSeed(seed) % FALLBACK_AVATAR_COLORS.length;
    const baseColor = FALLBACK_AVATAR_COLORS[colorIndex];
    const startColor = alpha(lighten(baseColor, 0.12), 0.8);
    const endColor = alpha(darken(baseColor, 0.14), 0.8);

    return `linear-gradient(135deg, ${startColor}, ${endColor})`;
  }, [src, fallbackSeedId, fallbackSeedName]);

  return (
    <Box onClick={onClick} className={classes.avatarWrapper}>
      <Avatar
        src={src}
        className={clsx(
          className,
          classes[`color_${border}`],
          type === AvatarTypes.square ? classes.squareIcon : classes.roundIcon,
        )}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundImage: fallbackBackgroundImage,
        }}>
        {children}
      </Avatar>
    </Box>
  );
};

export default AppAvatar;
